import React, { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import SurveyContent from './components/SurveyContent';
import Settings from './components/Settings';
import Login from './components/Login';
import ScrollToTop from './components/ScrollToTop';
import { surveyData as fallbackSurveyData } from './data/surveyData';
import { api } from './services/api';
import { API_URL } from './config';

// Lazy load heavy components
const Dashboard = lazy(() => import('./components/Dashboard'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));

function App() {
  const [user, setUser] = useState(null);
  const [surveyData, setSurveyData] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [responses, setResponses] = useState({});
  const [comments, setComments] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [appTitle, setAppTitle] = useState('Engineering Maturity Assessment');

  useEffect(() => {
    const savedTitle = localStorage.getItem('appCustomTitle');
    if (savedTitle) {
      setAppTitle(savedTitle);
    }
  }, []);

  const checkAuth = async () => {
    try {
      console.log('Checking authentication...');
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Auth check response:', data);
        console.log('User from auth check:', data.user);
        
        if (!data.user) {
          console.error('No user in auth response');
          setIsLoading(false);
          return;
        }
        
        setUser(data.user);
        console.log('Calling initializeApp with user:', data.user);
        await initializeApp(data.user);
      } else {
        console.log('Not authenticated, showing login');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsLoading(false);
    }
  };

  const handleLogin = async (userData) => {
    console.log('handleLogin called with userData:', userData);
    if (!userData) {
      console.error('userData is null/undefined in handleLogin');
      alert('Login failed: No user data received');
      return;
    }
    setUser(userData);
    setIsLoading(true);
    await loadSurveyStructure();
    await initializeSession(userData);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
      setResponses({});
      setComments({});
      setSessionId(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const initializeApp = async (userData) => {
    await loadSurveyStructure();
    await initializeSession(userData);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Update page title based on current view
  useEffect(() => {
    if (!user) {
      document.title = `Login - ${appTitle}`;
    } else if (showAdminPanel) {
      document.title = `Admin Panel - ${appTitle}`;
    } else if (showDashboard) {
      document.title = `Dashboard - ${appTitle}`;
    } else if (isSettingsOpen) {
      document.title = `Settings - ${appTitle}`;
    } else {
      document.title = appTitle;
    }
  }, [user, showAdminPanel, showDashboard, isSettingsOpen, appTitle]);

  const loadSurveyStructure = async () => {
    try {
      const response = await fetch(`${API_URL}/api/survey-structure`);
      const data = await response.json();
      
      if (data.surveyData && data.surveyData.length > 0) {
        setSurveyData(data.surveyData);
        setSelectedDomain(data.surveyData[0]);
      } else {
        setSurveyData(fallbackSurveyData);
        setSelectedDomain(fallbackSurveyData[0]);
      }
    } catch (error) {
      console.error('Error loading survey structure:', error);
      setSurveyData(fallbackSurveyData);
      setSelectedDomain(fallbackSurveyData[0]);
    }
  };

  const initializeSession = async (userData) => {
    try {
      if (!userData || !userData.id) {
        console.error('No user data available for session initialization');
        setIsLoading(false);
        return;
      }

      const userSessionKey = `surveySessionId_${userData.id}`;
      let currentSessionId = localStorage.getItem(userSessionKey);
      
      console.log('Initializing session for user:', userData.id, 'Existing session:', currentSessionId);
      
      if (!currentSessionId) {
        currentSessionId = `session_${userData.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        console.log('Creating new session:', currentSessionId);
        
        const response = await fetch(`${API_URL}/api/sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ sessionId: currentSessionId })
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to create session');
        }
        
        localStorage.setItem(userSessionKey, currentSessionId);
        console.log('Session created and saved to localStorage');
      }
      
      setSessionId(currentSessionId);
      console.log('Session ID set:', currentSessionId);
      
      const data = await api.getSessionData(currentSessionId);
      if (data.responses) {
        setResponses(data.responses);
        console.log('Loaded responses:', Object.keys(data.responses).length);
      }
      if (data.comments) {
        setComments(data.comments);
        console.log('Loaded comments:', Object.keys(data.comments).length);
      }
    } catch (error) {
      console.error('Error initializing session:', error);
      alert('Failed to initialize session. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDomainSelect = (domain) => {
    setSelectedDomain(domain);
  };

  const handleResponseChange = async (questionId, value) => {
    console.log('=== RESPONSE CHANGE ===');
    console.log('Question ID:', questionId);
    console.log('Value:', value);
    console.log('Current sessionId:', sessionId);
    console.log('Current user:', user);
    console.log('======================');
    
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
    
    if (!sessionId) {
      console.error('❌ No session ID available');
      alert('Session not initialized. Please refresh the page.');
      return;
    }
    
    try {
      const result = await api.saveResponse(sessionId, questionId, value);
      console.log('✅ Response saved successfully:', result);
    } catch (error) {
      console.error('❌ Error saving response:', error);
      alert('Failed to save response: ' + error.message);
    }
  };

  const handleCommentChange = async (questionId, comment) => {
    setComments(prev => ({
      ...prev,
      [questionId]: comment
    }));
    
    if (sessionId) {
      try {
        await api.saveComment(sessionId, questionId, comment);
      } catch (error) {
        console.error('Error saving comment:', error);
      }
    }
  };

  const calculateProgress = () => {
    const totalQuestions = surveyData.reduce((acc, domain) => {
      const domainQuestions = domain.subcategories 
        ? domain.subcategories.flatMap(sub => sub.questions)
        : domain.questions || [];
      return acc + domainQuestions.length;
    }, 0);
    const answeredQuestions = Object.keys(responses).length;
    return Math.round((answeredQuestions / totalQuestions) * 100);
  };

  const handleResetSurvey = async () => {
    if (sessionId && user) {
      try {
        await api.deleteSession(sessionId);
        const userSessionKey = `surveySessionId_${user.id}`;
        localStorage.removeItem(userSessionKey);
        setResponses({});
        setComments({});
        window.location.reload();
      } catch (error) {
        console.error('Error resetting survey:', error);
      }
    }
  };

  const handleOpenSettings = () => {
    setIsSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
  };

  const handleOpenDashboard = () => {
    setShowDashboard(true);
  };

  const handleCloseDashboard = () => {
    setShowDashboard(false);
  };

  const handleLogoClick = () => {
    setShowDashboard(false);
    setShowAdminPanel(false);
    setIsSettingsOpen(false);
    if (surveyData.length > 0 && !selectedDomain) {
      setSelectedDomain(surveyData[0]);
    }
  };

  const handleOpenAdminPanel = () => {
    setShowAdminPanel(true);
  };

  const handleCloseAdminPanel = () => {
    setShowAdminPanel(false);
  };

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  if (isLoading || !surveyData.length || !selectedDomain) {
    return (
      <div className="app">
        <div className="loading-container">
          <p>Loading survey...</p>
        </div>
      </div>
    );
  }

  if (showAdminPanel) {
    return (
      <Suspense fallback={
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Admin Panel...</p>
        </div>
      }>
        <AdminPanel onClose={handleCloseAdminPanel} />
      </Suspense>
    );
  }

  if (showDashboard) {
    return (
      <Suspense fallback={
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Dashboard...</p>
        </div>
      }>
        <Dashboard sessionId={sessionId} onClose={handleCloseDashboard} />
      </Suspense>
    );
  }

  return (
    <div className="app">
      <div className="app-header">
        <button onClick={handleLogoClick} className="logo-button" title="Go to Home">
          <img src="/ahead-logo.png" alt="AHEAD Logo" className="app-logo" />
        </button>
        <div className="header-title">
          <h1>{appTitle}</h1>
        </div>
        <div className="header-right">
          <div className="user-info">
            <span className="username">{user.username}</span>
            {user.role === 'admin' && <span className="admin-badge">Admin</span>}
          </div>
          <div className="progress-indicator">
            Progress: {calculateProgress()}% ({Object.keys(responses).length}/{surveyData.reduce((acc, d) => {
              const domainQuestions = d.subcategories 
                ? d.subcategories.flatMap(sub => sub.questions)
                : d.questions || [];
              return acc + domainQuestions.length;
            }, 0)} questions)
          </div>
          <button className="dashboard-icon-btn" onClick={handleOpenDashboard} title="View Dashboard">
            <svg className="dashboard-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
            </svg>
          </button>
          {user.role === 'admin' && (
            <button className="admin-icon-btn" onClick={handleOpenAdminPanel} title="Admin Panel">
              <svg className="admin-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </button>
          )}
          {user.role === 'admin' && (
            <button className="settings-icon-btn" onClick={handleOpenSettings} title="Settings">
              <svg className="settings-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="21" x2="4" y2="14" />
                <line x1="4" y1="10" x2="4" y2="3" />
                <line x1="12" y1="21" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12" y2="3" />
                <line x1="20" y1="21" x2="20" y2="16" />
                <line x1="20" y1="12" x2="20" y2="3" />
                <line x1="1" y1="14" x2="7" y2="14" />
                <line x1="9" y1="8" x2="15" y2="8" />
                <line x1="17" y1="16" x2="23" y2="16" />
              </svg>
            </button>
          )}
          <button className="logout-btn" onClick={handleLogout} title="Logout">
            <svg className="logout-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </div>
      <div className="app-container">
        <Sidebar 
          domains={surveyData}
          selectedDomain={selectedDomain}
          onDomainSelect={handleDomainSelect}
          responses={responses}
        />
        <SurveyContent 
          domain={selectedDomain}
          responses={responses}
          onResponseChange={handleResponseChange}
          comments={comments}
          onCommentChange={handleCommentChange}
        />
      </div>
      <Settings
        isOpen={isSettingsOpen}
        onClose={handleCloseSettings}
        sessionId={sessionId}
        onResetSurvey={handleResetSurvey}
      />
      <ScrollToTop />
    </div>
  );
}

export default App;
